/**
 * DrawingToolbar.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Drawing tools bar — left vertical panel alongside chart canvas.
 * 7 tools: Level, Trend, Fib, Zone, aVWAP, Ray, Measure.
 *
 * Architecture rules:
 *   - Zero internal state
 *   - Vertical layout: each tool is a 28×28px icon button
 *   - Active tool highlighted with teal pill + colour
 *   - Tooltip on hover shows full tool name
 *   - Adding a new drawing tool = add one entry to TOOLS here
 *     + one branch in chartRenderers.js renderDrawings()
 *     + one entry in DRAWING_COLORS in chartConfig.js
 *   - Clear button sits below a divider at the bottom
 *
 * Layout change (v8 HOTFIX-6):
 *   Moved from bottom horizontal bar → left vertical bar.
 *   Zero height cost — the bar sits beside the canvas, not below it.
 *   Scales to any number of future tools without wrapping or overflow.
 * ─────────────────────────────────────────────────────────────────────────────
 */

const TOOLS = [
  { key: 'horizontal',    label: 'Level',   icon: '—'  },
  { key: 'trendline',     label: 'Trend',   icon: '↗'  },
  { key: 'fibonacci',     label: 'Fib',     icon: 'Fib' },
  { key: 'rectangle',     label: 'Zone',    icon: '▭'  },
  { key: 'anchored_vwap', label: 'aVWAP',   icon: 'V'  },
  { key: 'ray',           label: 'Ray',     icon: '→'  },
  { key: 'measure',       label: 'Measure', icon: '↔'  },
];

export default function DrawingToolbar({ drawingMode, onDrawingModeChange, onClearDrawings }) {
  return (
    <div style={{
      width: 32,
      flexShrink: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 2,
      padding: '6px 0',
      borderRight: '1px solid rgba(255,255,255,0.05)',
      background: 'rgba(5,13,26,0.6)',
    }}>

      {TOOLS.map(t => {
        const active = drawingMode === t.key;
        return (
          <button
            key={t.key}
            onClick={() => onDrawingModeChange(active ? null : t.key)}
            title={t.label}
            style={{
              width: 26, height: 26,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: 5,
              fontFamily: 'inherit', fontSize: 11, fontWeight: active ? 700 : 400,
              cursor: 'pointer',
              border: `1px solid ${active ? 'rgba(0,212,255,0.5)' : 'transparent'}`,
              background: active ? 'rgba(0,212,255,0.12)' : 'transparent',
              color: active ? '#00d4ff' : '#4a5568',
              transition: 'all 0.12s',
              lineHeight: 1,
            }}
            onMouseEnter={e => { if (!active) { e.currentTarget.style.color = '#8b8ba7'; e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}}
            onMouseLeave={e => { if (!active) { e.currentTarget.style.color = '#4a5568'; e.currentTarget.style.background = 'transparent'; }}}
          >
            {t.icon}
          </button>
        );
      })}

      {/* Divider + clear button */}
      <div style={{ width: 18, height: 1, background: 'rgba(255,255,255,0.07)', margin: '4px 0' }} />
      <button
        onClick={onClearDrawings}
        title="Clear all drawings"
        style={{
          width: 26, height: 26,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          borderRadius: 5, fontFamily: 'inherit', fontSize: 11,
          cursor: 'pointer', border: '1px solid transparent',
          background: 'transparent', color: '#3a3a5c',
          transition: 'all 0.12s',
        }}
        onMouseEnter={e => { e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.background = 'rgba(239,68,68,0.08)'; }}
        onMouseLeave={e => { e.currentTarget.style.color = '#3a3a5c'; e.currentTarget.style.background = 'transparent'; }}
      >
        ✕
      </button>
    </div>
  );
}
